import { alertController, toastController } from '@ionic/core';
import { UserSession } from 'common';
import { AuthService, getAccessToken } from './AuthService';

let refreshPromise: Promise<UserSession | null> = null;


export async function expiryInterceptor(response: Response, config: RequestInit) {
  console.log('expiryInterceptor()', config.headers);
  const configHeaders = config.headers;
  await refreshSessionOrShowAlert(response.url);

  // Modify the original request to include the new token
  if (configHeaders instanceof Headers) {
    configHeaders.set('Authorization', `Bearer ${getAccessToken()}`);
  } else {
    configHeaders['Authorization'] = `Bearer ${getAccessToken()}`;
  }
  config.headers = configHeaders;

  // Retry the original request with the refreshed token
  return await fetch(response.url, config);
}

export async function refreshSessionOrShowAlert(error: string = null): Promise<UserSession | null> {
  // If already refreshing, wait for the existing promise
  if (refreshPromise) {
    return refreshPromise;
  }

  debugJWT(getAccessToken());

  // Start new refresh operation
  refreshPromise = (async () => {
    try {
      const session = await AuthService.refreshToken();
      AuthService.setSessionData(session);
      return session;
    } catch (error) {
      if (error.message && error.message.includes('connection')) {
        showConnectionError(error.message);
      } else {
        showExpiryAlert(error.message || error || "Unknown error");
      }
      return null;
    } finally {
      // Reset after a delay to prevent rapid successive calls
      setTimeout(() => {
        refreshPromise = null;
      }, 1000);
    }
  })();

  return refreshPromise;
}

var connectionErrorToast: HTMLIonToastElement;
async function showConnectionError(error: string) {
  connectionErrorToast = await toastController.create({
    message: 'Connection error: ' + error,
    duration: 300000,
    color: 'danger',
  }).then(toast => toast.present()) as HTMLIonToastElement;
  connectionErrorToast.onDidDismiss().then(() => {
    connectionErrorToast = null;
  });
}

async function showExpiryAlert(error: string) {
  console.log('showExpiryAlert()', error);
  if (window.location.hash?.includes('login')) {
    return;
  }

  debugJWT(getAccessToken());

  // show an alert for the user to re-login
  let modal = await alertController.create({
    header: 'Your session has expired',
    message: 'Failed to refresh token: ' + error,
    backdropDismiss: false,
    buttons: [
      {
        text: 'Cancel',
        role: 'cancel',
        handler: () => {
          console.log('Alert canceled');
        },
      },
      {
        text: 'OK',
        role: 'confirm',
        handler: () => {
          AuthService.logout();
          window.location.href = '/#/login';
        },
      },
    ],
  });
  modal.present();
}

function debugJWT(token: string): void {
  try {
    if (!token) {
      console.warn('No JWT token found');
      return;
    }
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT format - expected 3 parts, got', parts.length);
      return;
    }

    // Decode header (base64url decode)
    const header = JSON.parse(base64UrlDecode(parts[0]));
    console.log('🔍 JWT Header:', header);

    // Decode payload (base64url decode)
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    console.log('🔍 JWT Payload:', payload);

    // Check expiration
    if (payload.exp) {
      const expDate = new Date(payload.exp * 1000);
      const now = new Date();
      const timeUntilExpiry = expDate.getTime() - now.getTime();
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60));

      console.log('⏰ JWT Expires:', expDate.toISOString());
      console.log('⏰ Current Time:', now.toISOString());
      console.log('⏰ Time until expiry:', minutesUntilExpiry, 'minutes');

      if (timeUntilExpiry < 0) {
        console.error('❌ JWT is EXPIRED!');
      } else if (timeUntilExpiry < 5 * 60 * 1000) { // Less than 5 minutes
        console.warn('⚠️ JWT expires soon (less than 5 minutes)');
      } else {
        console.log('✅ JWT is valid');
      }
    } else {
      console.warn('⚠️ JWT has no expiration claim (exp)');
    }

    // Log other useful claims
    if (payload.sub) console.log('👤 Subject:', payload.sub);
    if (payload._id) console.log('🆔 User ID:', payload._id);
    if (payload['_couchdb.roles']) console.log('🔐 CouchDB Roles:', payload['_couchdb.roles']);
    if (payload.iat) console.log('📅 Issued at:', new Date(payload.iat * 1000).toISOString());

  } catch (error) {
    console.error('❌ Error decoding JWT:', error);
  }
}


/**
 * Base64URL decode utility (JWT uses base64url, not standard base64)
 * @param str - Base64URL encoded string
 * @returns Decoded string
 */
function base64UrlDecode(str: string): string {
  // Add padding if needed
  str += '='.repeat((4 - str.length % 4) % 4);
  // Replace URL-safe characters with standard base64 characters
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  // Decode
  return atob(str);
}


export function authenticationInterceptor(_url: string, config: RequestInit) {
  const configHeaders = config.headers || {};

  if (globalThis.user) {
    if (configHeaders instanceof Headers) {
      configHeaders.set('authorization', `Bearer ${getAccessToken()}`);
    } else {
      configHeaders['authorization'] = `Bearer ${getAccessToken()}`;
    }
  }

  config.headers = configHeaders;
  return config;
}
